import { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import {
  setLoading,
  setError,
  setCurrentForest,
  setTrees,
  addTree,
  updateTree,
  clearForest,
} from '../store/slices/forestSlice';
import {
  getForestByClassCode,
  getForestTrees,
  plantTree,
  waterTree,
  subscribeToForestTrees,
  updateTreeGrowthStages,
} from '../services/firestore';
import { updateUser } from '../services/firestore';
import { updateUser as updateUserAction } from '../store/slices/authSlice';
import { 
  startMonitoringCurrentForest, 
  stopMonitoringCurrentForest 
} from '../services/perForestMonitor';
import { activityManager } from '../services/activityManager';


export const useForest = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentForest, trees, isLoading, error } = useSelector((state: RootState) => state.forest);
  
  const [unsubscribeFromTrees, setUnsubscribeFromTrees] = useState<(() => void) | null>(null);
  const [listenerActive, setListenerActive] = useState(false);

  const loadForest = useCallback(async (classCode: string) => {
    try {
      dispatch(setLoading(true));
      
      console.log('Loading forest for class code:', classCode);
      const forest = await getForestByClassCode(classCode);
      
      if (forest) {
        console.log('Forest loaded:', forest.name);
        dispatch(setCurrentForest(forest));
        
        const forestTrees = await getForestTrees(forest.id);
        console.log(`Loaded ${forestTrees.length} trees from subcollection`);
        dispatch(setTrees(forestTrees));
        
        if (user?.uid) {
          console.log(`Starting per-forest monitoring for user ${user.uid} in forest ${forest.id}`);
          startMonitoringCurrentForest(forest.id, user.uid);
        }
        
      } else {
        console.log('No forest found for class code:', classCode);
        dispatch(setError(`No active forest found for class code: ${classCode}`));
      }
    } catch (error) {
      console.error('Error loading forest:', error);
      dispatch(setError(error instanceof Error ? error.message : 'Failed to load forest'));
    }
  }, [dispatch, user?.uid]);

  const handlePlantTree = useCallback(async (x: number, y: number, chosenColor?: string) => {
    if (!user || !currentForest || user.seeds <= 0) {
      throw new Error('Cannot plant tree: missing user, forest, or seeds');
    }

    console.log('=== PLANTING TREE START ===');
    console.log('Position:', { x, y });
    console.log('User seeds before:', user.seeds);
    console.log('Forest ID:', currentForest.id);

    try {
      const newTree = await plantTree(
        currentForest.id,
        user.uid,
        `${user.firstName} ${user.lastName}`,
        { x, y },
        chosenColor
      );

      console.log('Tree planted with ID:', newTree.id);

      const newSeedCount = user.seeds - 1;
      const newTotalPlanted = user.totalSeedsPlanted + 1;

      dispatch(updateUserAction({
        seeds: newSeedCount,
        totalSeedsPlanted: newTotalPlanted
      }));

      updateUser(user.uid, {
        seeds: newSeedCount,
        totalSeedsPlanted: newTotalPlanted,
        updatedAt: new Date()
      }).catch(console.error);

      console.log('User seeds after:', newSeedCount);
      console.log('=== PLANTING TREE END ===');

      return newTree;

    } catch (error) {
      console.error('Error planting tree:', error);
      throw error;
    }
  }, [user, currentForest, dispatch]);


    //Set up tree growth automation

  useEffect(() => {
    if (!currentForest?.id) return;

    console.log('Setting up activity-aware tree growth automation for forest:', currentForest.id);
    
    //update on forest load
    if (activityManager.isActive()) {
      updateTreeGrowthStages(currentForest.id)
        .then(() => console.log('Initial tree growth update completed'))
        .catch(error => console.error('Initial tree growth update failed:', error));
    }
    
    //Update when active
    const growthInterval = setInterval(async () => {
      if (activityManager.isActive()) {
        try {
          console.log('Running periodic tree growth update');
          await updateTreeGrowthStages(currentForest.id);
        } catch (error) {
          console.error('Error in tree growth automation:', error);
        }
      } else {
        console.log('Skipping tree growth update - user inactive');
      }
    }, 60000); //SETTING: Check every minute

    return () => {
      console.log('Cleaning up tree growth automation');
      clearInterval(growthInterval);
    };
  }, [currentForest?.id]);

  const handleWaterTree = useCallback(async (treeId: string) => {
    if (!user || !currentForest || user.water <= 0) {
      throw new Error('Cannot water tree: missing user, forest, or water');
    }

    console.log('=== WATERING TREE START ===');
    console.log('Tree ID:', treeId);
    console.log('User water before:', user.water);
    console.log('Forest ID:', currentForest.id);

    try {
      const targetTree = trees.find(tree => tree.id === treeId);
      if (!targetTree) {
        throw new Error('Tree not found');
      }

      const isOwnTree = targetTree.planterId === user.uid;
      console.log('Is own tree:', isOwnTree);
      console.log('Tree owner:', targetTree.planterName);

      const updatedTree = await waterTree(
        currentForest.id,
        treeId,
        user.uid,
        `${user.firstName} ${user.lastName}`
      );

      console.log('Tree watered successfully');

      const newWaterCount = user.water - 1;
      const newTotalWaterUsed = isOwnTree ? user.totalWaterUsedOnOthers : user.totalWaterUsedOnOthers + 1;

      dispatch(updateUserAction({
        water: newWaterCount,
        totalWaterUsedOnOthers: newTotalWaterUsed
      }));

      updateUser(user.uid, {
        water: newWaterCount,
        totalWaterUsedOnOthers: newTotalWaterUsed,
        updatedAt: new Date()
      }).catch(console.error);

      console.log('User water after:', newWaterCount);
      console.log('=== WATERING TREE END ===');

      return updatedTree;

    } catch (error) {
      console.error('Error watering tree:', error);
      throw error;
    }
  }, [user, currentForest, trees, dispatch]);

  useEffect(() => {
    if (user && user.classCode && !currentForest && !isLoading) {
      console.log('Auto-loading forest for user class code:', user.classCode);
      loadForest(user.classCode);
    }
  }, [user, currentForest, isLoading, loadForest]);

  useEffect(() => {
    if (!user && currentForest) {
      console.log('Clearing forest on logout');
      
      if (unsubscribeFromTrees) {
        console.log('Cleaning up tree subscription on logout');
        unsubscribeFromTrees();
        setUnsubscribeFromTrees(null);
        setListenerActive(false);
      }
      
      stopMonitoringCurrentForest(currentForest.id, 'unknown-user');
      
      dispatch(clearForest());
    }
  }, [user, currentForest, unsubscribeFromTrees, dispatch]);

  useEffect(() => {
    return () => {
      if (unsubscribeFromTrees) {
        console.log('useForest cleanup: stopping tree subscription');
        unsubscribeFromTrees();
        setListenerActive(false);
      }
      
      if (currentForest?.id && user?.uid) {
        console.log(`Stopping per-forest monitoring on unmount`);
        stopMonitoringCurrentForest(currentForest.id, user.uid);
      }
    };
  }, [unsubscribeFromTrees, currentForest?.id, user?.uid]);

  useEffect(() => {
    if (!currentForest?.id || !user?.uid) {
      console.log('No forest ID or user, skipping subscription setup');
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let activityCheckInterval: NodeJS.Timeout | null = null;

    const startTreeSubscription = () => {
      if (unsubscribe) return;
      
      console.log('Starting real-time tree subscription for forest:', currentForest.id);
      setListenerActive(true);
      
      unsubscribe = subscribeToForestTrees(
        currentForest.id,
        (updatedTrees) => {
          if (activityManager.isActive()) {
            console.log(`REAL-TIME UPDATE: ${updatedTrees.length} trees received`);
            dispatch(setTrees(updatedTrees));
          } else {
            console.log('Ignoring tree update - user inactive');
          }
        },
        (error) => {
          console.error('Real-time trees error:', error);
          dispatch(setError(error.message));
        }
      );
    };

    const stopTreeSubscription = () => {
      if (!unsubscribe) return;
      
      console.log('Stopping real-time tree subscription');
      unsubscribe();
      unsubscribe = null;
      setListenerActive(false);
    };

    if (activityManager.isActive()) {
      startTreeSubscription();
    } else {
      console.log('User inactive on mount, no tree subscription started');
    }

    activityCheckInterval = setInterval(() => {
      const isUserActive = activityManager.isActive();
      const hasActiveSubscription = unsubscribe !== null;

      if (isUserActive && !hasActiveSubscription) {
        console.log('User became active, starting tree subscription');
        startTreeSubscription();
      } else if (!isUserActive && hasActiveSubscription) {
        console.log('User became inactive, stopping tree subscription');
        stopTreeSubscription();
      }
    }, 30000);

    setUnsubscribeFromTrees(() => () => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
        setListenerActive(false);
      }
    });
    
    return () => {
      console.log('Tree subscription effect cleanup');
      if (unsubscribe) {
        unsubscribe();
        setListenerActive(false);
      }
      if (activityCheckInterval) {
        clearInterval(activityCheckInterval);
      }
    };
  }, [currentForest?.id, user?.uid, dispatch]);

  const getTreeAt = useCallback((x: number, y: number) => {
    return trees.find(tree => tree.position.x === x && tree.position.y === y) || null;
  }, [trees]);

  const hasTreeAt = useCallback((x: number, y: number) => {
    return trees.some(tree => tree.position.x === x && tree.position.y === y);
  }, [trees]);

  const getTreesByUser = useCallback((userId: string) => {
    return trees.filter(tree => tree.planterId === userId);
  }, [trees]);

  const getUserTreeStats = useCallback((userId: string) => {
    const userTrees = getTreesByUser(userId);
    const matureTrees = userTrees.filter(tree => tree.maturityStage >= 5);
    
    return {
      totalTrees: userTrees.length,
      matureTrees: matureTrees.length,
      treesByStage: {
        seed: userTrees.filter(tree => tree.maturityStage === 0).length,
        sprout: userTrees.filter(tree => tree.maturityStage === 1).length,
        seedling: userTrees.filter(tree => tree.maturityStage === 2).length,
        sapling: userTrees.filter(tree => tree.maturityStage === 3).length,
        young: userTrees.filter(tree => tree.maturityStage === 4).length,
        mature: userTrees.filter(tree => tree.maturityStage === 5).length,
      }
    };
  }, [getTreesByUser]);

  const getForestStats = useCallback(() => {
    const matureTrees = trees.filter(tree => tree.maturityStage >= 5);
    const totalWaterUsed = trees.reduce((sum, tree) => sum + tree.waterReceived, 0);
    
    return {
      totalTrees: trees.length,
      matureTrees: matureTrees.length,
      totalWaterUsed,
      averageMaturityStage: trees.length > 0 
        ? trees.reduce((sum, tree) => sum + tree.maturityStage, 0) / trees.length 
        : 0,
      treesByStage: {
        seed: trees.filter(tree => tree.maturityStage === 0).length,
        sprout: trees.filter(tree => tree.maturityStage === 1).length,
        seedling: trees.filter(tree => tree.maturityStage === 2).length,
        sapling: trees.filter(tree => tree.maturityStage === 3).length,
        young: trees.filter(tree => tree.maturityStage === 4).length,
        mature: trees.filter(tree => tree.maturityStage === 5).length,
      }
    };
  }, [trees]);

  return {
    currentForest,
    trees,
    isLoading,
    error,
    listenerActive,
    
    loadForest,
    handlePlantTree,
    handleWaterTree,
    
    getTreeAt,
    hasTreeAt,
    getTreesByUser,
    getUserTreeStats,
    getForestStats,
    hasForest: !!currentForest,
  };
};